/**
 * French Tax Calculator Service
 * Implements Article 150 VH bis of the French General Tax Code (CGI)
 *
 * Formula: Plus-value = Prix_cession - (Prix_cession × Prix_acquisition_portefeuille / Valeur_globale_portefeuille)
 *
 * References:
 * - Article 150 VH bis CGI: https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000050366751
 * - BOFiP: https://bofip.impots.gouv.fr/bofip/11969-PGP.html
 */

import {
  DEFAULT_FRENCH_TAX_RATES,
  getFrenchTaxRatesForYear,
} from '../data/frenchTaxRates';

export type TransactionType = 'purchase' | 'sale' | 'swap_in' | 'swap_out' | 'staking_reward' | 'mining' | 'airdrop' | 'gift_received' | 'gift_sent';

export interface TaxableEvent {
  id: string;
  date: string;                    // ISO date string
  type: TransactionType;
  asset: string;                   // Token symbol (BTC, ETH, etc.)
  quantity: number;
  pricePerUnit: number;            // Price in EUR at transaction time
  totalValue: number;              // Total value in EUR
  fees?: number;                   // Transaction fees in EUR
  exchange?: string;               // Exchange name
  txHash?: string;                 // Blockchain transaction hash
  notes?: string;
  /**
   * V2.E1b — opt-in income tax classification for reward events
   * (staking_reward, mining, airdrop). When set :
   *   - 'PFU' : reward is treated as RCM (Art. 200 A CGI), taxed at
   *             PFU 30% on the totalValue at perception. Adds to
   *             `summary.rewardsIncomeRCM` + `summary.pfuOnRewardsIncome`.
   *   - 'BNC' : reward is treated as BNC (Art. 92 CGI), the caller
   *             must apply their own marginalRate. Adds to
   *             `summary.rewardsIncomeBNC` + `summary.bncRewardsBase`.
   * When `undefined` (default), the calculator stays neutral on the
   * income-tax side : the reward still increases the cost basis (so
   * future capital gains compute correctly) but no income tax is
   * computed. The user is then responsible for declaring the income
   * outside this surface.
   *
   * The default is intentionally `undefined` — French doctrine on
   * staking / mining / airdrop varies with the blockchain, the
   * activity frequency, and the perception context. The calculator
   * does NOT pick a regime on the user's behalf.
   */
  incomeRegime?: 'PFU' | 'BNC';
}

export interface TaxableSale {
  event: TaxableEvent;
  salePrice: number;               // Prix de cession (A)
  acquisitionPrice: number;        // Part du prix d'acquisition (A × B / C)
  capitalGain: number;             // Plus-value ou moins-value
  portfolioValueAtSale: number;    // Valeur globale du portefeuille (C)
  totalAcquisitionCost: number;    // Prix total d'acquisition (B)
}

export interface TaxSummary {
  year: number;
  totalSales: number;              // Nombre de cessions
  totalSaleValue: number;          // Total des cessions en €
  totalCapitalGains: number;       // Plus-values brutes
  totalCapitalLosses: number;      // Moins-values brutes
  netCapitalGain: number;          // Plus-value nette (gains - pertes)
  isExempt: boolean;               // Exonéré si < 305€
  exemptionThreshold: number;      // Seuil d'exonération (305€)
  taxableAmount: number;           // Montant imposable
  flatTax: number;                 // PFU 30%
  incomeTaxPortion: number;        // Part IR 12.8%
  socialContributions: number;     // Prélèvements sociaux 17.2%
  sales: TaxableSale[];            // Détail des cessions
  stakingRewards: number;          // Total revenus staking (toutes catégories)
  miningRewards: number;           // Total revenus mining (toutes catégories)
  airdrops: number;                // Total airdrops (toutes catégories)
  // V2.E1b — opt-in income tax tracking on rewards (Art. 92 / 200 A
  // CGI). Populated only when `event.incomeRegime` is explicitly set
  // by the caller. Default-undefined rewards stay neutral and
  // contribute zero to these fields.
  rewardsIncomeRCM: number;        // Sum of rewards classified RCM (PFU)
  rewardsIncomeBNC: number;        // Sum of rewards classified BNC (progressive)
  pfuOnRewardsIncome: number;      // 30% × rewardsIncomeRCM
  bncRewardsBase: number;          // = rewardsIncomeBNC (caller applies marginalRate)
  // V2.E1c — losses carry-forward (Art. 150-0 D 12-bis CGI). Losses
  // up to 10 fiscal years old can offset capital gains of the current
  // year. Populated only when `options.previousYearLosses` is passed.
  previousLossCarryForwardEligible: number;  // Sum of losses still in window
  previousLossCarryForwardUsed: number;      // Used to compensate this year
  previousLossCarryForwardRemaining: number; // Eligible − used (still carryable)
}

/**
 * V2.E1c — options bag for `calculateForYear`. Optional to preserve
 * full backwards-compat with the original `calculateForYear(year)`
 * signature. Each new option is added as a new field on this
 * interface so callers stay forwards-compatible.
 */
export interface CalculateForYearOptions {
  /**
   * Net capital losses from previous fiscal years that the user wants
   * to carry forward. Each entry is `{ year, amount }` — `amount`
   * must be a **positive** number representing a loss (the calculator
   * negates internally when offsetting gains).
   *
   * Per Art. 150-0 D 12-bis CGI, only losses from the **10 latest
   * fiscal years** preceding the target year are eligible. Anything
   * older is filtered out automatically. The calculator does not
   * require the input to be sorted or deduplicated.
   */
  previousYearLosses?: Array<{ year: number; amount: number }>;

  /**
   * V2.E2 — pre-fetched historical price snapshots used by the
   * Article 150 VH bis formula to compute the global portfolio value
   * (`C` in the formula `A − A × B / C`) at the date of each sale or
   * gift. Indexed first by `asset` (token symbol matching
   * `TaxableEvent.asset`), then by ISO date `YYYY-MM-DD`.
   *
   * Replaces the previous 24h-window heuristic which silently
   * underestimated `C` for portfolios holding assets that had not
   * been traded recently — leading to inflated capital-gain figures
   * (cf. F6 in `docs/v2/TAX_FR_AUDIT.md`).
   *
   * The caller is responsible for sourcing the prices (CoinGecko
   * range API, local cache, or any other oracle). Coverage doesn't
   * have to be exhaustive : the calculator falls back to the legacy
   * heuristic for any (asset, date) miss, so partial snapshots are
   * a strict improvement on the baseline.
   *
   * @example
   *   priceSnapshots: {
   *     BTC: { '2024-06-01': 45000, '2024-12-25': 60000 },
   *     ETH: { '2024-06-01': 3200 },
   *   }
   */
  priceSnapshots?: Record<string, Record<string, number>>;
}

export interface PortfolioState {
  totalAcquisitionCost: number;    // Prix total d'acquisition
  holdings: Map<string, number>;   // Quantité par asset
  lastUpdate: string;
}

/**
 * French Tax Calculator
 * Calculates capital gains according to Article 150 VH bis CGI
 */
export class FrenchTaxCalculator {
  private events: TaxableEvent[];
  private portfolioState: PortfolioState;
  // V2.E2 — set during calculateForYear(), read by getPricesAtDate.
  // Never mutated. `null` means no snapshots passed → legacy
  // heuristic behaviour preserved.
  private priceSnapshots: Record<string, Record<string, number>> | null = null;

  // V2.E4 — Tax rates ARE year-keyed via `getFrenchTaxRatesForYear`
  // (`src/data/frenchTaxRates.ts`). The static fields below are kept
  // as backwards-compat aliases pointing to the default (latest)
  // year so existing consumers (tests, UI labels) keep compiling.
  // Internal computations in `calculateForYear` + `compareTaxRegimes`
  // route through the year-specific lookup.
  static readonly FLAT_TAX_RATE = DEFAULT_FRENCH_TAX_RATES.flatTaxRate;
  static readonly INCOME_TAX_RATE = DEFAULT_FRENCH_TAX_RATES.incomeTaxRate;
  static readonly SOCIAL_CONTRIBUTIONS_RATE = DEFAULT_FRENCH_TAX_RATES.socialContributionsRate;
  static readonly EXEMPTION_THRESHOLD = DEFAULT_FRENCH_TAX_RATES.exemptionThreshold;

  constructor(events: TaxableEvent[]) {
    this.events = [...events].sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    this.portfolioState = {
      totalAcquisitionCost: 0,
      holdings: new Map(),
      lastUpdate: '',
    };
  }

  /**
   * Calculate the global portfolio value at a specific date
   */
  private calculatePortfolioValue(date: string, prices: Map<string, number>): number {
    let totalValue = 0;

    for (const [asset, quantity] of this.portfolioState.holdings) {
      const price = prices.get(asset) || 0;
      totalValue += quantity * price;
    }

    return totalValue;
  }

  /**
   * Resolve prices for every held asset at the date of an event.
   *
   * Resolution order, per asset (V2.E2) :
   *  1. Current event's `pricePerUnit` wins for `currentEvent.asset`
   *     (the freshest source — this is the actual sale/gift price).
   *  2. `options.priceSnapshots[asset][YYYY-MM-DD]` if supplied + hit.
   *  3. Legacy 24h-window heuristic over neighbouring events.
   *
   * The map only contains entries we could actually resolve — any
   * asset with no source at all is omitted so `calculatePortfolioValue`
   * skips it (instead of multiplying by 0 and underestimating C).
   */
  private getPricesAtDate(date: string, currentEvent: TaxableEvent): Map<string, number> {
    const prices = new Map<string, number>();

    // Source 1 : current event's price for its own asset.
    prices.set(currentEvent.asset, currentEvent.pricePerUnit);

    const eventDate = new Date(date);
    const isoDate = date.slice(0, 10); // YYYY-MM-DD lookup key

    // Iterate every held asset, not just those visible in events —
    // ensures we look up snapshots for assets bought in earlier
    // years that may have no event close to `date`.
    for (const asset of this.portfolioState.holdings.keys()) {
      if (prices.has(asset)) continue;

      // Source 2 : caller-supplied snapshot.
      const snapshotPrice = this.priceSnapshots?.[asset]?.[isoDate];
      if (typeof snapshotPrice === 'number' && snapshotPrice > 0) {
        prices.set(asset, snapshotPrice);
        continue;
      }

      // Source 3 : legacy 24h-window heuristic over events. Walks
      // every event but only keeps the matching asset within ±24h.
      for (const evt of this.events) {
        if (evt.asset !== asset) continue;
        const otherEventDate = new Date(evt.date);
        if (Math.abs(otherEventDate.getTime() - eventDate.getTime()) < 86400000) {
          prices.set(asset, evt.pricePerUnit);
          break;
        }
      }
    }

    return prices;
  }

  /**
   * Process a purchase event
   */
  private processPurchase(event: TaxableEvent): void {
    // Add to acquisition cost
    const costWithFees = event.totalValue + (event.fees || 0);
    this.portfolioState.totalAcquisitionCost += costWithFees;

    // Add to holdings
    const currentQty = this.portfolioState.holdings.get(event.asset) || 0;
    this.portfolioState.holdings.set(event.asset, currentQty + event.quantity);

    this.portfolioState.lastUpdate = event.date;
  }

  /**
   * Process a sale event and calculate capital gain
   * Formula: Plus-value = A - (A × B / C)
   * Where:
   *   A = Prix de cession (sale price minus fees)
   *   B = Prix total d'acquisition du portefeuille
   *   C = Valeur globale du portefeuille
   */
  private processSale(event: TaxableEvent): TaxableSale {
    // A = Sale price minus fees
    const salePrice = event.totalValue - (event.fees || 0);

    // Get portfolio value at time of sale
    const prices = this.getPricesAtDate(event.date, event);
    const portfolioValue = this.calculatePortfolioValue(event.date, prices);

    // B = Total acquisition cost
    const totalAcquisitionCost = this.portfolioState.totalAcquisitionCost;

    // C = Portfolio value (must be > 0 to avoid division by zero)
    const portfolioValueAtSale = portfolioValue > 0 ? portfolioValue : salePrice;

    // Calculate acquisition price portion: A × B / C
    const acquisitionPricePortion = (salePrice * totalAcquisitionCost) / portfolioValueAtSale;

    // Capital gain = A - (A × B / C)
    const capitalGain = salePrice - acquisitionPricePortion;

    // Update portfolio state
    // Reduce acquisition cost proportionally
    const reductionRatio = salePrice / portfolioValueAtSale;
    this.portfolioState.totalAcquisitionCost *= (1 - reductionRatio);

    // Reduce holdings
    const currentQty = this.portfolioState.holdings.get(event.asset) || 0;
    this.portfolioState.holdings.set(event.asset, Math.max(0, currentQty - event.quantity));

    this.portfolioState.lastUpdate = event.date;

    return {
      event,
      salePrice,
      acquisitionPrice: acquisitionPricePortion,
      capitalGain,
      portfolioValueAtSale,
      totalAcquisitionCost,
    };
  }

  /**
   * Process a gift sent event.
   *
   * Per Article 150 VH bis CGI, transferring crypto to another person
   * "à titre gratuit" (gift / donation) is **NOT** a fait générateur
   * de plus-value. The user does not realise a capital gain, and the
   * gift must NOT be included in `summary.sales`.
   *
   * The gifted portion is still removed from the portfolio so future
   * sales' B/C ratios stay correct :
   *  - holdings reduced for the gifted asset,
   *  - total acquisition cost reduced proportionally to the gift's
   *    fair market value relative to the portfolio's global value
   *    (same prorata logic as a sale, minus the TaxableSale output).
   *
   * Out of scope of this calculator : "droits de mutation à titre
   * gratuit" (donation tax) which may apply depending on the
   * relationship between donor and recipient. That regime lives in
   * Art. 757 / 777 CGI and is handled by the donation-tax surface,
   * not here.
   *
   * Fixed in Sprint V2.E1a (cf. `docs/v2/TAX_FR_AUDIT.md` §F1).
   */
  private processGiftSent(event: TaxableEvent): void {
    // Fair market value of the gifted assets — caller must pass the
    // value at the time of the gift via `event.totalValue`.
    const giftValue = event.totalValue;

    // Compute portfolio value at gift time (same logic as processSale
    // for symmetry — degenerates to giftValue when no other assets).
    const prices = this.getPricesAtDate(event.date, event);
    const portfolioValue = this.calculatePortfolioValue(event.date, prices);
    const portfolioValueAtGift = portfolioValue > 0 ? portfolioValue : giftValue;

    // Reduce acquisition cost proportionally — preserves the formula's
    // invariant for future sales of the remaining assets.
    const reductionRatio = giftValue / portfolioValueAtGift;
    this.portfolioState.totalAcquisitionCost *= (1 - reductionRatio);

    // Reduce holdings.
    const currentQty = this.portfolioState.holdings.get(event.asset) || 0;
    this.portfolioState.holdings.set(
      event.asset,
      Math.max(0, currentQty - event.quantity),
    );

    this.portfolioState.lastUpdate = event.date;
  }

  /**
   * Process staking/mining rewards
   * These are treated as taxable income at market value when received
   */
  private processReward(event: TaxableEvent): void {
    // Add to holdings at current market value (acquisition cost = 0 for tax purposes in some interpretations)
    // However, for French tax, rewards are generally taxed as BNC if regular activity
    const currentQty = this.portfolioState.holdings.get(event.asset) || 0;
    this.portfolioState.holdings.set(event.asset, currentQty + event.quantity);

    // Add to acquisition cost at market value when received
    this.portfolioState.totalAcquisitionCost += event.totalValue;

    this.portfolioState.lastUpdate = event.date;
  }

  /**
   * Calculate taxes for a specific year
   */
  /**
   * Maximum age (in years) for a loss to be carry-forward eligible.
   * Per Art. 150-0 D 12-bis CGI, losses from more than 10 fiscal
   * years before the current one are forfeited.
   */
  static readonly LOSS_CARRY_FORWARD_MAX_YEARS = 10;

  calculateForYear(
    year: number,
    options: CalculateForYearOptions = {},
  ): TaxSummary {
    // Reset portfolio state
    this.portfolioState = {
      totalAcquisitionCost: 0,
      holdings: new Map(),
      lastUpdate: '',
    };
    // V2.E2 — install caller-supplied price snapshots (or null) for
    // the duration of this calculation. `getPricesAtDate` reads it
    // via `this.priceSnapshots`. Cleared at the end so subsequent
    // calls without snapshots fall back cleanly to the legacy path.
    this.priceSnapshots = options.priceSnapshots ?? null;

    const sales: TaxableSale[] = [];
    let stakingRewards = 0;
    let miningRewards = 0;
    let airdrops = 0;
    // V2.E1b — opt-in income tax buckets. Populated only when an
    // event has `incomeRegime` set explicitly. Default-undefined
    // rewards stay neutral (current behaviour preserved).
    let rewardsIncomeRCM = 0;
    let rewardsIncomeBNC = 0;

    // Process all events up to end of year
    for (const event of this.events) {
      const eventYear = new Date(event.date).getFullYear();

      // Process events before or during the target year
      if (eventYear <= year) {
        switch (event.type) {
          case 'purchase':
          case 'swap_in':
          case 'gift_received':
            this.processPurchase(event);
            break;

          case 'sale':
          case 'swap_out': {
            const sale = this.processSale(event);
            // Only include sales from the target year in the summary
            if (eventYear === year) {
              sales.push(sale);
            }
            break;
          }

          case 'gift_sent':
            // V2.E1a — gifts are NOT a fait générateur de plus-value
            // (Art. 150 VH bis CGI). Reduce holdings + acquisition
            // cost prorata, but do NOT push to `sales[]`.
            this.processGiftSent(event);
            break;

          case 'staking_reward':
            this.processReward(event);
            if (eventYear === year) {
              stakingRewards += event.totalValue;
              // V2.E1b — opt-in income tax classification.
              if (event.incomeRegime === 'PFU') rewardsIncomeRCM += event.totalValue;
              else if (event.incomeRegime === 'BNC') rewardsIncomeBNC += event.totalValue;
            }
            break;

          case 'mining':
            this.processReward(event);
            if (eventYear === year) {
              miningRewards += event.totalValue;
              if (event.incomeRegime === 'PFU') rewardsIncomeRCM += event.totalValue;
              else if (event.incomeRegime === 'BNC') rewardsIncomeBNC += event.totalValue;
            }
            break;

          case 'airdrop':
            this.processReward(event);
            if (eventYear === year) {
              airdrops += event.totalValue;
              if (event.incomeRegime === 'PFU') rewardsIncomeRCM += event.totalValue;
              else if (event.incomeRegime === 'BNC') rewardsIncomeBNC += event.totalValue;
            }
            break;
        }
      }
    }

    // Calculate totals
    const totalSaleValue = sales.reduce((sum, s) => sum + s.salePrice, 0);
    const totalCapitalGains = sales.reduce((sum, s) => sum + Math.max(0, s.capitalGain), 0);
    const totalCapitalLosses = sales.reduce((sum, s) => sum + Math.abs(Math.min(0, s.capitalGain)), 0);
    const netCapitalGain = totalCapitalGains - totalCapitalLosses;

    // V2.E1c — apply previous-year-losses carry forward (Art. 150-0 D
    // 12-bis CGI). Filter out losses older than the 10-year window or
    // dated in the future. Only positive amounts contribute. Eligible
    // losses can offset positive net capital gains (never below 0).
    const minEligibleYear = year - FrenchTaxCalculator.LOSS_CARRY_FORWARD_MAX_YEARS;
    const previousLossCarryForwardEligible = (options.previousYearLosses ?? [])
      .filter((entry) => entry.year >= minEligibleYear && entry.year < year && entry.amount > 0)
      .reduce((sum, entry) => sum + entry.amount, 0);

    // V2.E4 — fetch year-specific tax rates. Falls back to default
    // (latest year) when the year is missing from the table.
    const rates = getFrenchTaxRatesForYear(year);

    // Check exemption threshold (applies BEFORE the carry-forward
    // offset — exemption skips the entire taxation, no need to dip
    // into past losses).
    const isExempt = totalSaleValue <= rates.exemptionThreshold;
    const taxableAmountBeforeCarry = isExempt ? 0 : Math.max(0, netCapitalGain);
    const previousLossCarryForwardUsed = Math.min(
      previousLossCarryForwardEligible,
      taxableAmountBeforeCarry,
    );
    const previousLossCarryForwardRemaining =
      previousLossCarryForwardEligible - previousLossCarryForwardUsed;
    const taxableAmount = taxableAmountBeforeCarry - previousLossCarryForwardUsed;

    // Calculate taxes (V2.E4: year-specific rates).
    const flatTax = taxableAmount * rates.flatTaxRate;
    const incomeTaxPortion = taxableAmount * rates.incomeTaxRate;
    const socialContributions = taxableAmount * rates.socialContributionsRate;

    // V2.E1b — income tax on opt-in classified rewards.
    // PFU 30% applied to the RCM bucket directly; the BNC bucket is
    // returned as a base for the caller to apply their own marginalRate
    // (calculator stays neutral on the user's IR bracket).
    const pfuOnRewardsIncome = rewardsIncomeRCM * rates.flatTaxRate;
    const bncRewardsBase = rewardsIncomeBNC;

    // V2.E2 — clear the per-call snapshots so any later
    // `calculateForYear()` call without options falls back to the
    // legacy heuristic without leaking state.
    const summary = {
      year,
      totalSales: sales.length,
      totalSaleValue,
      totalCapitalGains,
      totalCapitalLosses,
      netCapitalGain,
      isExempt,
      exemptionThreshold: rates.exemptionThreshold,
      taxableAmount,
      flatTax,
      incomeTaxPortion,
      socialContributions,
      sales,
      stakingRewards,
      miningRewards,
      airdrops,
      rewardsIncomeRCM,
      rewardsIncomeBNC,
      pfuOnRewardsIncome,
      bncRewardsBase,
      previousLossCarryForwardEligible,
      previousLossCarryForwardUsed,
      previousLossCarryForwardRemaining,
    };
    this.priceSnapshots = null;
    return summary;
  }

  /**
   * Compare PFU vs Progressive Income Tax (option pour le barème
   * progressif IR — Art. 200 A 2 CGI). Returns which option is more
   * advantageous given the user's marginal rate.
   *
   * V2.E3 — extended to integrate rewards income (V2.E1b) :
   *
   *  - **Capital gains** : eligible to either PFU 30% or barème
   *    progressif (`marginalRate × taxableAmount + 17.2%
   *    socialContributions`). Contributes to both totals.
   *
   *  - **Rewards classified RCM** (PFU regime opted in via
   *    `event.incomeRegime = 'PFU'`) : eligible to either PFU 30% on
   *    the RCM bucket OR barème progressif (`marginalRate × bucket +
   *    17.2% PS`). Contributes to both totals.
   *
   *  - **Rewards classified BNC** (`event.incomeRegime = 'BNC'`) :
   *    **always** at barème progressif (BNC has no PFU option). The
   *    calculator only applies `marginalRate × bucket` here — actual
   *    BNC social regime (URSSAF / TNS) varies by activity status
   *    and is the caller's responsibility. Contributes to BOTH the
   *    "PFU option" and "progressive option" totals because BNC tax
   *    is unaffected by the option.
   *
   * The return value preserves the original 4 scalar fields
   * (`pfuAmount`, `progressiveAmount`, `recommendation`, `savings`)
   * for backwards-compat. A new `breakdown` field exposes the
   * itemised breakdown for callers that want to display a
   * transparent comparison table.
   */
  compareTaxRegimes(taxSummary: TaxSummary, marginalTaxRate: number): {
    pfuAmount: number;
    progressiveAmount: number;
    recommendation: 'pfu' | 'progressive';
    savings: number;
    breakdown: {
      capitalGains: { pfu: number; progressive: number };
      rewardsRCM: { pfu: number; progressive: number };
      rewardsBNC: { progressive: number };
    };
  } {
    // V2.E4 — fetch year-specific rates via taxSummary.year. The PS
    // rate notably appears in both PFU and progressive paths for
    // RCM, so it must match the year that produced the summary.
    const rates = getFrenchTaxRatesForYear(taxSummary.year);

    // Capital gains : both regimes apply social 17.2 % equivalently;
    // the choice changes only the IR portion.
    const cgPfu = taxSummary.flatTax;
    const cgProgressive = taxSummary.taxableAmount * marginalTaxRate
      + taxSummary.socialContributions;

    // Rewards RCM : same shape as capital gains. Already-computed
    // pfuOnRewardsIncome is reused so the math is consistent.
    const rcmPfu = taxSummary.pfuOnRewardsIncome;
    const rcmProgressive = taxSummary.rewardsIncomeRCM * marginalTaxRate
      + taxSummary.rewardsIncomeRCM * rates.socialContributionsRate;

    // Rewards BNC : always progressive on the IR side (no PFU option
    // for BNC). Social regime is caller-defined → only IR is added
    // here. Contributes equally to both totals so the comparison
    // isolates the choice's impact (capital gains + RCM).
    const bncProgressive = taxSummary.bncRewardsBase * marginalTaxRate;

    const pfuAmount = cgPfu + rcmPfu + bncProgressive;
    const progressiveAmount = cgProgressive + rcmProgressive + bncProgressive;

    const recommendation = pfuAmount <= progressiveAmount ? 'pfu' : 'progressive';
    const savings = Math.abs(pfuAmount - progressiveAmount);

    return {
      pfuAmount,
      progressiveAmount,
      recommendation,
      savings,
      breakdown: {
        capitalGains: { pfu: cgPfu, progressive: cgProgressive },
        rewardsRCM: { pfu: rcmPfu, progressive: rcmProgressive },
        rewardsBNC: { progressive: bncProgressive },
      },
    };
  }

  /**
   * Generate Form 2086 data
   */
  generateForm2086Data(taxSummary: TaxSummary): {
    cessions: {
      date: string;
      asset: string;
      prixCession: number;
      prixAcquisition: number;
      plusValue: number;
    }[];
    totals: {
      totalPlusValues: number;
      totalMoinsValues: number;
      plusValueNette: number;
      montantImposable: number;
    };
  } {
    return {
      cessions: taxSummary.sales.map(sale => ({
        date: sale.event.date,
        asset: sale.event.asset,
        prixCession: sale.salePrice,
        prixAcquisition: sale.acquisitionPrice,
        plusValue: sale.capitalGain,
      })),
      totals: {
        totalPlusValues: taxSummary.totalCapitalGains,
        totalMoinsValues: taxSummary.totalCapitalLosses,
        plusValueNette: taxSummary.netCapitalGain,
        montantImposable: taxSummary.taxableAmount,
      },
    };
  }

  /**
   * Generate Form 3916-bis data (foreign accounts declaration)
   */
  generateForm3916BisData(events: TaxableEvent[]): {
    accounts: {
      exchange: string;
      country: string;
      openedDate: string;
      closedDate: string | null;
      accountType: 'custodial' | 'non_custodial';
    }[];
  } {
    const exchanges = new Map<string, { firstDate: string; lastDate: string }>();

    for (const event of events) {
      if (event.exchange) {
        const existing = exchanges.get(event.exchange);
        if (!existing) {
          exchanges.set(event.exchange, {
            firstDate: event.date,
            lastDate: event.date,
          });
        } else {
          if (event.date < existing.firstDate) existing.firstDate = event.date;
          if (event.date > existing.lastDate) existing.lastDate = event.date;
        }
      }
    }

    // Map of known exchanges and their countries
    const exchangeCountries: Record<string, string> = {
      'Binance': 'Cayman Islands',
      'Coinbase': 'United States',
      'Kraken': 'United States',
      'Crypto.com': 'Singapore',
      'KuCoin': 'Seychelles',
      'Bitget': 'Singapore',
      'OKX': 'Seychelles',
      'Bybit': 'Singapore',
      'Gate.io': 'Cayman Islands',
    };

    return {
      accounts: Array.from(exchanges.entries()).map(([exchange, dates]) => ({
        exchange,
        country: exchangeCountries[exchange] || 'Unknown',
        openedDate: dates.firstDate,
        closedDate: null,
        accountType: 'custodial' as const, // CEX are custodial
      })),
    };
  }
}

/**
 * Utility: Format currency for French locale
 */
export function formatEUR(amount: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

/**
 * Utility: Format date for French locale
 */
export function formatDateFR(date: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(date));
}
